/**
 * A user's status, changeable from its list row. COPIED from the status-select-in-a-cell of
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/list/columns.tsx
 * (the row's own status is the control), built as the rizzui `Dropdown` of
 * vendor/isomorphic/apps/isomorphic/src/app/shared/support/inbox/action-dropdown.tsx, the same
 * shape as `orders/order-state-menu.tsx`. It offers the moves Identity allows from that status. Whether a move is permitted (own account, last administrator, a Super Admin's
 * account) is Identity's call; a refusal comes back as its own words in a toast.
 */
import { useState } from "react";
import { useRevalidator } from "react-router-dom";
import toast from "react-hot-toast";
import { PiCaretDownBold, PiLockOpenDuotone, PiPlayCircleDuotone, PiProhibitDuotone } from "react-icons/pi";
import { Dropdown } from "rizzui";
import { cn, getStatusBadge } from "@steelyard/theme";
import { deactivateUser, reactivateUser, unlockUser, userStatusLabel, type UserSummary } from "../../lib/services/identity";
import { problemMessage } from "../dashboard/tone";
import { useConfirm } from "../confirm-modal";

export function UserStatusMenu({ user, isSelf, mayActivate = false }: { user: UserSummary; isSelf: boolean; mayActivate?: boolean }) {
  const revalidator = useRevalidator();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const name = user.displayName ?? user.email ?? "this account";
  const badge = getStatusBadge(userStatusLabel(user.status));

  // Not for an invited (pending) account: revoking the invitation is how an invite is withdrawn.
  const canDeactivate = user.status === "active";
  const canReactivate = user.status === "deactivated";
  // `company.user.administer` only: an invited account made active before acceptance. The invitation
  // link stays the way its owner sets a password (Identity's accept handler allows exactly this case).
  const canActivate = mayActivate && user.status === "pending";
  if (isSelf || (!canDeactivate && !canReactivate && !canActivate && !user.locked)) return badge;

  async function apply(change: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await change();
      toast.success(`${name}: ${done}`);
      revalidator.revalidate();
    } catch (err) {
      toast.error(problemMessage(err, `${name}: Infra Junction refused that change`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dropdown placement="bottom-start">
      <Dropdown.Trigger
        disabled={busy}
        aria-label={`Change the status of ${name} (now ${userStatusLabel(user.status)})`}
        className={cn(
          "group inline-flex items-center gap-1.5 rounded-md border border-transparent px-1.5 py-1 -ms-1.5 transition-colors hover:border-muted hover:bg-gray-50",
          busy && "animate-pulse",
        )}
      >
        {badge}
        <PiCaretDownBold aria-hidden className="size-3 text-gray-400 group-hover:text-gray-700" />
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-52">
        {canActivate ? (
          <Dropdown.Item onClick={() => apply(() => reactivateUser(user.id), "activated")} className="gap-2.5 text-sm text-green-dark">
            <PiPlayCircleDuotone aria-hidden className="size-4 shrink-0" />
            Activate
          </Dropdown.Item>
        ) : null}
        {canReactivate ? (
          <Dropdown.Item onClick={() => apply(() => reactivateUser(user.id), "reactivated")} className="gap-2.5 text-sm text-green-dark">
            <PiPlayCircleDuotone aria-hidden className="size-4 shrink-0" />
            Reactivate
          </Dropdown.Item>
        ) : null}
        {user.locked ? (
          <Dropdown.Item onClick={() => apply(() => unlockUser(user.id), "unlocked")} className="gap-2.5 text-sm">
            <PiLockOpenDuotone aria-hidden className="size-4 shrink-0 text-gray-500" />
            Unlock
          </Dropdown.Item>
        ) : null}
        {canDeactivate ? (
          <Dropdown.Item
            onClick={() =>
              confirm({
                title: `Deactivate ${name}`,
                consequence: "All of its sessions are signed out immediately. It can be reactivated later.",
                confirmLabel: "Deactivate",
                onConfirm: () => void apply(() => deactivateUser(user.id), "deactivated"),
              })
            }
            className="gap-2.5 text-sm text-red-dark"
          >
            <PiProhibitDuotone aria-hidden className="size-4 shrink-0" />
            Deactivate
          </Dropdown.Item>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
}
